import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, MessageCircle, Banknote, Star, Share2 } from "lucide-react";
import { CATEGORY_LABELS } from "@/utils/whatsapp";
import { shareProduct } from "@/utils/share";
import { PLACEHOLDER } from "@/utils/imageUrl";

export const ProductModal = ({ product, images, sizes, selectedSize, onSelectSize, onWhatsApp, onApartar, onClose }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const savings = product.retail_price > product.sale_price ? product.retail_price - product.sale_price : 0;
  const prev = () => setIdx(i => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setIdx(i => (i === images.length - 1 ? 0 : i + 1));

  return createPortal(
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="product-modal-overlay">
      <div
        className="glass-card w-full sm:max-w-4xl max-h-[95vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl grid grid-cols-1 md:grid-cols-2"
        onClick={(e) => e.stopPropagation()}
        data-testid={`product-modal-${product.id}`}
      >
        {/* Gallery */}
        <div className="relative aspect-[3/4] md:aspect-auto md:min-h-[520px] bg-[#0a0a0a] group/img">
          <img src={images[idx]} alt={product.name} onError={(e) => { e.target.src = PLACEHOLDER; }} className="w-full h-full object-cover md:rounded-l-3xl" data-testid="product-modal-image" />
          <button onClick={onClose} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 md:hidden" data-testid="product-modal-close-mobile">
            <X className="w-5 h-5" />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="product-modal-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="product-modal-next"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 px-3 overflow-x-auto scrollbar-hide">
                {images.map((img, i) => (
                  <button key={img} onClick={() => setIdx(i)} className={`w-12 h-12 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${i === idx ? "border-[#00FF66]" : "border-white/20 opacity-70"}`} data-testid={`product-modal-thumb-${i}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
          {product.exclusive && (
            <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-[#050505] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
              <Star className="w-3 h-3 fill-current" /> Exclusivo
            </div>
          )}
        </div>

        {/* Details */}
        <div className="p-6 sm:p-8 flex flex-col">
          <div className="hidden md:flex justify-end -mt-2 -mr-2 mb-2 gap-1">
            <button onClick={() => shareProduct(product)} className="text-[#A0A0A0] hover:text-[#B026FF] transition-colors p-2" title="Compartir" data-testid="product-modal-share">
              <Share2 className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="text-[#A0A0A0] hover:text-white transition-colors p-2" data-testid="product-modal-close">
              <X className="w-6 h-6" />
            </button>
          </div>
          {product.brand && <p className="text-sm text-[#B026FF] uppercase tracking-wider font-semibold mb-1">{product.brand}</p>}
          <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="product-modal-name">{product.name}</h2>
          <p className="text-sm text-[#A0A0A0] mb-5">
            {CATEGORY_LABELS[product.category] || product.category}
            {product.retail_price > 0 && <> &bull; Precio en tienda USA: <span className="line-through">${Number(product.retail_price).toFixed(0)}</span></>}
          </p>

          <div className="flex items-end gap-3 mb-6">
            <span className="font-oswald text-4xl font-bold text-[#00FF66]" data-testid="product-modal-price">${Number(product.sale_price).toFixed(0)}</span>
            <span className="text-sm text-[#A0A0A0] mb-1.5">MXN</span>
            {savings > 0 && <span className="mb-1.5 bg-[#00FF66]/10 text-[#00FF66] text-xs font-bold px-2.5 py-1 rounded-full">Ahorras ${savings.toFixed(0)}</span>}
          </div>

          {sizes.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-white font-semibold mb-2">Elige tu talla</p>
              <div className="flex flex-wrap gap-2" data-testid="product-modal-sizes">
                {sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => onSelectSize(selectedSize === size ? "" : size)}
                    className={`min-w-[3.5rem] px-4 py-2.5 rounded-xl border text-base font-bold transition-all ${selectedSize === size ? "bg-[#00FF66] text-[#050505] border-[#00FF66]" : "bg-[#050505] text-white border-white/10 hover:border-white/40"}`}
                    data-testid={`product-modal-size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#A0A0A0] mt-2">{selectedSize ? `Talla elegida: ${selectedSize}` : "Toca una talla (opcional)"}</p>
            </div>
          )}

          <div className="mt-auto space-y-3">
            <button onClick={onWhatsApp} className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 font-bold text-base uppercase tracking-wide flex items-center justify-center gap-2 rounded-full transition-all hover:shadow-[0_0_16px_rgba(37,211,102,0.4)]" data-testid="product-modal-whatsapp-btn">
              <MessageCircle className="w-5 h-5" /> Comprar por WhatsApp
            </button>
            <button onClick={onApartar} className="w-full border border-[#B026FF] text-[#B026FF] hover:bg-[#B026FF] hover:text-white py-4 font-bold text-base uppercase tracking-wide flex items-center justify-center gap-2 rounded-full transition-all" data-testid="product-modal-payment-btn">
              <Banknote className="w-5 h-5" /> Apartar con transferencia
            </button>
            <p className="text-xs text-[#A0A0A0] text-center">Entrega en persona. Sin envios.</p>
            <button onClick={() => shareProduct(product)} className="w-full text-[#B026FF] text-sm font-semibold flex items-center justify-center gap-2 py-2 hover:underline md:hidden" data-testid="product-modal-share-mobile">
              <Share2 className="w-4 h-4" /> Compartir con un amigo
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
